import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {type FC, Fragment, useEffect} from 'react';
import ZoomMapToExtentButton from './ZoomToMapExtentButton.tsx';
import {useGetConfigurationCollections} from '@src/api/configurations';
import ErrorMessage from '@src/components/common/ErrorMessage.tsx';
import LoadingMessage from '@src/components/common/LoadingMessage.tsx';
import {notifyWarning} from '@src/components/notifier/notify.tsx';
import {useCollectionExtentInView} from '@src/hooks/useCollectionExtentInView.ts';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions.ts';

const CollectionSelection: FC = () => {
  const {configurationId, collectionId, setCollectionId, setSuperResParams} =
    useVisualizationParams();
  const {collections, isLoading, isError, error, failedCollections} =
    useGetConfigurationCollections(configurationId);

  const failedIds = failedCollections?.map(c => c).join(',');
  useEffect(() => {
    if (failedIds && !isLoading) {
      notifyWarning({
        data: {
          title: 'Failed to load collections',
          message: failedIds,
        },
        toastId: failedIds,
      });
    }
  }, [failedIds, isLoading]);
  const selectedCollectionId = collectionId ?? collections?.[0]?.id;
  const doesIntersectWithView = useCollectionExtentInView(selectedCollectionId);

  if (isLoading) {
    return (
      <LoadingMessage rootProps={{sx: {blockSize: 'auto', paddingBlock: 2}}} />
    );
  }

  if (isError && error) {
    return (
      <ErrorMessage>
        <Typography variant="h6" color="inherit" gutterBottom>
          {error.message}
        </Typography>
      </ErrorMessage>
    );
  }
  return (
    <Box
      sx={{
        marginInline: `${2 * dimensions.leftSidebar.contentMargin}px`,
        paddingBlockEnd: 2,
        paddingBlockStart: 0.5,
      }}
    >
      <Typography
        component="label"
        sx={{fontSize: 12, color: 'text.secondary', paddingBlockEnd: 1}}
        id="collections-radio-buttons-group-label"
      >
        Data Collection
      </Typography>
      <RadioGroup
        aria-labelledby="collections-radio-buttons-group-label"
        value={selectedCollectionId ?? collections?.[0]?.id}
        onChange={async (_e, id) => {
          await setCollectionId(id, '');
          setSuperResParams(null);
        }}
        name="radio-buttons-group"
      >
        {collections.map(collection => {
          const colId = collection.id;
          const collectionName = collection.title ?? colId;
          return (
            <Fragment key={colId}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  value={colId}
                  control={<Radio />}
                  sx={{flexGrow: 1}}
                  label={
                    <Typography variant="caption">{collectionName}</Typography>
                  }
                />
                {colId === selectedCollectionId ? (
                  <ZoomMapToExtentButton
                    collectionId={colId}
                    component={IconButton}
                    enablePulse={!doesIntersectWithView}
                  />
                ) : (
                  <Box sx={{width: 28, height: 28}} />
                )}
              </Box>
              <Divider sx={{width: '100%'}} />
            </Fragment>
          );
        })}
      </RadioGroup>
    </Box>
  );
};

export default CollectionSelection;
